/* @HEADER
 * @version 1.1.1434 | 2026-11-01
 * @purpose The panel about ONE project that the gear in the project's row opens (ZVX-DEV-8842, ZVX-5311 П1): its first section is «Баги» — the project's bug settings, drawn by the same components and the same host as the board toolbar's — and for a project that is not the open one a line that says to open it first and the button that does.
 * @sidecar ProjectSettingsPanel.tsx.header.md
 * @layer app | @hex adapter | @ctx kanban
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8842, ZVX-DEV-8690

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { BugSettingsSection } from '@zvenix/tasks'
import { CardKindRulesHost } from '../features/kanban/CardKindRulesHost'
import { useMessages } from '../shared/i18n/useMessages'
import { PROJECT_SWITCHER_TESTIDS } from './project-switcher.testids'
import { clampPanelPlace } from './projectSettingsPanelPlace'

export interface ProjectSettingsPanelProps {
  projectName: string
  /** True for the project that is open on this device: only its settings are held here (a colleague receives those of a project that is not open only once somebody opens it). */
  isOpenProject: boolean
  /** Where the box stands: fixed, by the gear that opened it. */
  place: { top: number; left: number }
  onOpenProject: () => void
  /** Escape. */
  onClose: () => void
}

export function ProjectSettingsPanel({ projectName, isOpenProject, place, onOpenProject, onClose }: ProjectSettingsPanelProps) {
  const m = useMessages()
  const t = m.projectSwitcher
  const box = useRef<HTMLDivElement>(null)
  // Beside the gear, then moved into the window once the box is measured (a phone's window is narrower than the box, and the gear is at its right).
  const [stands, setStands] = useState(place)
  useLayoutEffect(() => {
    const rect = box.current?.getBoundingClientRect()
    if (rect && rect.width > 0) setStands(clampPanelPlace(place, { width: rect.width, height: rect.height }, { width: window.innerWidth, height: window.innerHeight }))
  }, [place])
  // The focus goes into the panel when it opens, so Escape reaches it and a keyboard user is not left behind the dropdown.
  useEffect(() => { box.current?.focus() }, [])
  return createPortal(
    <div
      ref={box}
      tabIndex={-1}
      role="group"
      aria-label={t.settingsTitle.replace('{name}', projectName)}
      className="project-switcher__settings-portal"
      data-testid={PROJECT_SWITCHER_TESTIDS.settingsPanel}
      style={{ position: 'fixed', top: stands.top, left: stands.left }}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
    >
      <div className="project-switcher__settings-head">
        <div className="project-switcher__settings-title">{t.settingsTitle.replace('{name}', projectName)}</div>
        <button
          type="button"
          className="project-switcher__settings-close"
          data-testid={PROJECT_SWITCHER_TESTIDS.settingsClose}
          aria-label={t.settingsClose}
          title={t.settingsClose}
          onClick={onClose}
        >{'\u2715'}</button>
      </div>
      {isOpenProject ? (
        <div className="project-switcher__settings-section" data-testid={PROJECT_SWITCHER_TESTIDS.settingsBugsSection}>
          <CardKindRulesHost>
            <BugSettingsSection kinds={m.kanban?.kinds} onClose={onClose} />
          </CardKindRulesHost>
        </div>
      ) : (
        <div className="project-switcher__settings-section">
          <p className="project-switcher__settings-open-first" data-testid={PROJECT_SWITCHER_TESTIDS.settingsOpenFirst}>{t.settingsOpenFirst}</p>
          <button type="button" className="project-switcher__settings-open-btn" data-testid={PROJECT_SWITCHER_TESTIDS.settingsOpenBtn} onClick={onOpenProject}>
            {t.settingsOpenBtn}
          </button>
        </div>
      )}
    </div>,
    document.body,
  )
}
